// Banner Tracking Utilities
// Integrated with GA4 and MongoDB analytics

// Get API URL for MongoDB tracking
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'sportsguru247.com' || 
        hostname.includes('deploy.emergentcf.cloud') ||
        hostname.includes('sportsguru')) {
      return '/api';
    }
  }
  return process.env.REACT_APP_BACKEND_URL 
    ? `${process.env.REACT_APP_BACKEND_URL}/api` 
    : '/api';
};

// Store for tracking data (can be synced to backend later)
let trackingData = {
  impressions: [],
  clicks: [],
};

// Get device type
export const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Generate unique session ID
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('banner_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('banner_session_id', sessionId);
  }
  return sessionId;
};

// Send to MongoDB analytics
const sendToMongoDB = async (eventType, data) => {
  try {
    const API = getApiUrl();
    await fetch(`${API}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        page: data.pageUrl || window.location.pathname,
        element_id: data.bannerId,
        element_text: data.slotId,
        metadata: {
          destination_url: data.destinationUrl,
          device_type: data.deviceType,
          session_id: data.sessionId
        }
      })
    });
  } catch (e) {
    console.debug('MongoDB tracking error:', e);
  }
};

// Track banner impression
export const trackImpression = (bannerId, slotId, pageUrl) => {
  const impression = {
    event: 'banner_impression',
    bannerId,
    slotId,
    pageUrl,
    deviceType: getDeviceType(),
    sessionId: getSessionId(),
    timestamp: new Date().toISOString(),
  };
  
  trackingData.impressions.push(impression);
  
  // Log for debugging (remove in production)
  console.log('[Banner Tracking] Impression:', impression);
  
  // GA4 tracking
  if (window.gtag) {
    window.gtag('event', 'banner_impression', {
      banner_id: bannerId,
      slot_id: slotId,
      device_type: getDeviceType(),
    });
  }
  
  // Custom tracking hook
  if (window.onBannerImpression) {
    window.onBannerImpression(impression);
  }
  
  return impression;
};

// Track banner click
export const trackClick = (bannerId, slotId, pageUrl, destinationUrl) => {
  const deviceType = getDeviceType();
  
  // Map slot to CTA name
  const ctaNameMap = {
    'top_banner': 'sticky_desktop',
    'mid_page_banner': 'mid_content',
    'sticky_footer_banner': 'sticky_mobile',
    'inline_banner': 'mid_content',
  };
  const ctaName = ctaNameMap[slotId] || (deviceType === 'mobile' ? 'sticky_mobile' : 'sticky_desktop');
  
  // Get match name from page
  const matchName = document.querySelector('h1')?.innerText || document.title.replace(/\s*[-|].*$/, '') || 'SportsGuru247';
  
  const click = {
    event: 'banner_click',
    event_type: 'click',
    match_name: matchName,
    cta_name: ctaName,
    device_type: deviceType,
    bannerId,
    slotId,
    pageUrl,
    destinationUrl,
    sessionId: getSessionId(),
    timestamp: new Date().toISOString(),
  };
  
  trackingData.clicks.push(click);
  
  // Debug log with proper structure
  console.log('TRACK EVENT:', {
    type: 'click',
    match: matchName,
    cta: ctaName,
    device_type: deviceType,
  });
  
  // GA4 tracking with proper structure
  if (window.gtag) {
    window.gtag('event', 'click', {
      match_name: matchName,
      cta_name: ctaName,
      device_type: deviceType,
      banner_id: bannerId,
      destination_url: destinationUrl,
    });
  }
  
  // Reddit tracking
  if (window.rdt) {
    const isFunBet = destinationUrl.includes('funbet');
    window.rdt('track', isFunBet ? 'Lead' : 'ViewContent', {
      customEventName: `click_${ctaName}`,
      value: matchName,
    });
  }
  
  // Send to MongoDB with proper structure
  sendToMongoDB('click', {
    ...click,
    metadata: {
      match_name: matchName,
      cta_name: ctaName,
      device_type: deviceType,
    }
  });
  
  // Custom tracking hook
  if (window.onBannerClick) {
    window.onBannerClick(click);
  }
  
  return click;
};

// Get tracking data summary
export const getTrackingData = () => ({
  ...trackingData,
  summary: {
    totalImpressions: trackingData.impressions.length,
    totalClicks: trackingData.clicks.length,
    clickRate: trackingData.impressions.length > 0 
      ? (trackingData.clicks.length / trackingData.impressions.length * 100).toFixed(2) + '%'
      : '0%',
  },
});

// Reset tracking data (for testing)
export const resetTrackingData = () => {
  trackingData = { impressions: [], clicks: [] };
};

// Get click counts per banner
export const getClicksByBanner = () => {
  const counts = {};
  trackingData.clicks.forEach(click => {
    counts[click.bannerId] = (counts[click.bannerId] || 0) + 1;
  });
  return counts;
};

// Get impressions by slot
export const getImpressionsBySlot = () => {
  const counts = {};
  trackingData.impressions.forEach(imp => {
    counts[imp.slotId] = (counts[imp.slotId] || 0) + 1;
  });
  return counts;
};

export default {
  trackImpression,
  trackClick,
  getTrackingData,
  getDeviceType,
};
